import { DateNormalizer } from '../date_utils/normalizer'
import { getLastImportedOn } from '../helpers/date_helpers'
import Anchor from './anchor'

export class PeriodStrategy {
  /**
   * @param {string|Date|DateTime} dt - A date string, JS Date, or Luxon DateTime
   * @param {object} options
   */
  constructor(dt, options) {
    this.date = DateNormalizer.normalize(dt)
    this.options = options || {}
    this.position = options?.position || 0

    this.anchor = new Anchor(dt)
  }

  compareYear(count) {
    let anc = this.anchor.shift({ years: -count })
    return new this.constructor(anc.date, { size: this.size })
  }

  comparePeriod(count) {
    let factor = (this.dayCount * count) + count
    let anc = this.anchor.shift({ days: -factor })

    return new DayStrategy(anc.date, { size: this.dayCount })
  }

  compare(comparison, count = 1) {
    return comparison === 'period' ? this.comparePeriod(count) : this.compareYear(count)
  }

  get dateMin() {
    return this.anchor.date.minus({ days: this.size })
  }

  get dateMax() { return this.anchor.date }

  get dateMinStr() { return this.dateMin.toISODate() }
  get dateMaxStr() { return this.dateMax.toISODate() }

  get range() {
    return [this.dateMin, this.dateMax]
  }

  get size() {
    return this.options['size'] || this.defaultSize
  }

  get defaultSize() { return 1 }

  get period() {
    return this.options['period'] || this.defaultPeriodName
  }

  get defaultPeriodName() { return 'l30d' }

  get offset() {
    return this.#slice(this.options, this.units)
  }

  get dayCount() {
    // return Math.trunc(this.dateMax.diff(this.dateMin).as('days'))
    return this.size
  }

  get units() {
    return ['days', 'weeks', 'months', 'quarters', 'years']
  }

  #slice(obj, keys) {
    return Object.fromEntries(keys.filter(k => k in obj).map(k => [k, obj[k]]))
  }
}

export class DayStrategy extends PeriodStrategy {
  get dateMin() {
    return this.anchor.shift({ days: -this.size }).date
  }

  // get dateMax() { return this.anchor.date }

  comparePeriod(count = 1) {
    let factor = (this.size * count) + count
    let anc = this.anchor.shift({ days: -factor })

    return new this.constructor(anc.date, { size: this.size })
  }
}

export class TrailingMonthStrategy extends PeriodStrategy {
  get dateMin() {
    return this.dateMax.minus({ months: (this.size - 1) }).startOf('month')
  }

  get dateMax() {
    return this.anchor.trailingEnd
  }

  comparePeriod(count) {
    let factor = this.size * count
    let anc = this.anchor.shift({ months: -factor, days: -1 })
    return new this.constructor(anc.date, { size: this.size })
  }

  get defaultPeriodName() { return 't12m' }
}

export class MtdStrategy extends PeriodStrategy {
  get dateMin() {
    return this.anchor.date.startOf('month')
  }

  get dayCount() {
    return Math.trunc(this.dateMax.diff(this.dateMin).as('days'))
  }
}

export class QtdStrategy extends PeriodStrategy {
  get dateMin() {
    return this.anchor.date.startOf('quarter')
  }

  get dayCount() {
    return Math.trunc(this.dateMax.diff(this.dateMin).as('days'))
  }
}

export class YtdStrategy extends PeriodStrategy {
  get dateMin() {
    return this.anchor.date.startOf('year')
  }

  /**
   * Use calendar-day math so DST/timezone offsets do not change the result.
   */
  get dayCount() {
    const max = this.dateMax
    const min = this.dateMin
    return (Date.UTC(max.year, max.month - 1, max.day) - Date.UTC(min.year, min.month - 1, min.day)) / 86400000
  }
}

export const periodIndex = {
  "TrailingMonthStrategy": ['ttm', 't12m'],
  "YtdStrategy": ['ytd'],
  "QtdStrategy": ['qtd'],
  "MtdStrategy": ['mtd'],
  "DayStrategy": ['l30d', 'l60d', 'l90d'],
}

const classMap = {
  'DayStrategy': DayStrategy,
  'TrailingMonthStrategy': TrailingMonthStrategy,
  'MtdStrategy': MtdStrategy,
  'QtdStrategy': QtdStrategy,
  'YtdStrategy': YtdStrategy,
}

function constantize(className) {
  return classMap[className]
}

export class PeriodBuilder {
  static build(periodName, anchorDate) {
    const builder = new PeriodBuilder()
    return builder.build(periodName, anchorDate)
  }

  constructor() {
    this.periodData = app.periodData.data
  }

  build(periodName, anchorDate) {
    const size = this.periodData[periodName].offset
    const stratClass = this.periodStratMap[periodName]
    const dt = anchorDate || getLastImportedOn()

    return new stratClass(dt, { period: periodName, size: size })
  }

  get periodStratMap() {
    return {
      l30d: DayStrategy,
      l60d: DayStrategy,
      l90d: DayStrategy,
      t12m: TrailingMonthStrategy,
      mtd: MtdStrategy,
      qtd: QtdStrategy,
      ytd: YtdStrategy
    }
  }
}

export class RangeSet {
  constructor(leaderStrategy, options = {}) {
    this.leader = leaderStrategy
    this.leader.position ||= 1
    this.followCount = options.followCount ?? options.follow_count ?? 1
    this.comparison = options.comparison ?? 'year'
    this._followers = null
  }

  *[Symbol.iterator]() {
    yield* this.ranges
  }

  find(position) {
    return this.ranges.find((range) => Number(range.position) === Number(position))
  }

  get followers() {
    this._followers ||= this.buildFollowers()
    return this._followers
  }

  get ranges() {
    return [this.leader, ...this.followers]
  }

  get last() {
    return this.ranges[this.ranges.length - 1]
  }

  buildFollowers() {
    const ranges = []

    for (let idx = 1; idx <= this.followCount; idx += 1) {
      const strat = this.leader.compare(this.comparison)
      strat.position = idx + 1
      ranges.push(strat)
    }

    return ranges
  }
}
